// @ts-check

/**
 * @file Renders the comment editor screen.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import {useLocalSearchParams, useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"
import buildRoutePath from "../../shared/build-route-path"
import Button from "../../components/button"
import Card from "../../components/card"
import {Comment} from "../../frontend-models/comment"
import errorMessage from "../../shared/error-message"
import FormSelect from "../../components/form/form-select"
import FormTextInput from "../../components/form/form-text-input"
import {optionalRouteParam, requiredRouteParam} from "../../shared/route-params"
import {Task} from "../../frontend-models/task"
import Text from "@kaspernj/api-maker/build/utils/text"
import {taskStateOptions, translatedTaskState} from "../../shared/task-state"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {useScreenLoading} from "../../components/board-layout/screen-loading"
import {useProjectSyncOnline} from "../../sync/project-sync-connectivity"
import {useActiveProjectReplica} from "../../sync/project-sync-lifecycle"
import {queueOfflineCommentCreate, queueOfflineCommentCreateWithStateChange} from "../../database/project-replica-comment-commands"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} CommentsEditScreenProps */

/**
 * @typedef {object} CommentsEditScreenState
 * @property {Comment | null} comment Loaded comment record.
 * @property {boolean} loading Whether editor data is loading.
 * @property {boolean} saving Whether the comment is saving.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 * @property {Task | null} task Loaded task for new task comments.
 * @property {string | null} taskState Current task state selection.
 */

/**
 * Comment edit/new screen.
 * @augments {ShapeComponent<CommentsEditScreenProps, CommentsEditScreenState>}
 */
class CommentsEditScreen extends ShapeComponent {
  /** @type {string | null} */
  commentID
  /** @type {string} */
  defaultComment = ""
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {string | null} */
  resourceID
  /** @type {string | null} */
  resourceType
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** Owning project replica id, resolved once the task is loaded (drives the offline queue). */
  replicaProjectId = /** @type {string | null} */ null
  /** Active project replica, present when the offline queue can accept the write. */
  replica = null
  /** @type {boolean} */
  online = true
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  /** @type {CommentsEditScreenState} */
  state = {
    comment: /** @type {Comment | null} */ null,
    loading: true,
    saving: false,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    task: /** @type {Task | null} */ null,
    taskState: /** @type {string | null} */ null
  }

  /** Initializes route params and comment editor loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const commentID = optionalRouteParam(params, "id", "Comment ID")
    const resourceType = commentID
      ? optionalRouteParam(params, "resourceType", "Resource type")
      : requiredRouteParam(params, "resourceType", "Resource type")
    const resourceID = commentID
      ? optionalRouteParam(params, "resourceId", "Resource ID")
      : requiredRouteParam(params, "resourceId", "Resource ID")
    // The replica is resolved from the owning project. The route carries the
    // project id as a query param on most comment flows; that is a stable hook
    // key, so the active-replica lookup re-resolves to the project replica.
    const projectIdParam = optionalRouteParam(params, "projectId", "Project ID")
    this.commentID = commentID
    this.defaultComment = optionalRouteParam(params, "defaultComment", "Default comment") ?? ""
    this.params = params
    this.resourceID = resourceID
    this.resourceType = resourceType
    this.router = router
    this.replicaProjectId = projectIdParam
    this.replica = useActiveProjectReplica(projectIdParam ?? "")
    this.online = useProjectSyncOnline()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          if (commentID) {
            const loadedComment = await Comment.find(commentID)
            if (cancelled) return
            const nextResourceType = loadedComment.resourceType()
            const nextResourceId = loadedComment.resourceId()
            if (nextResourceType === "Task" && nextResourceId) {
              const loadedTask = await Task.find(nextResourceId)
              if (cancelled) return
              this.s.comment = loadedComment
              this.s.loading = false
              this.s.task = loadedTask
              this.s.taskState = loadedTask.state()
            } else {
              this.s.comment = loadedComment
              this.s.loading = false
            }
          } else if (resourceType === "Task" && resourceID) {
            const loadedTask = await Task.find(resourceID)
            if (cancelled) return
            this.s.loading = false
            this.s.task = loadedTask
            this.s.taskState = loadedTask.state()
          } else {
            this.s.loading = false
          }
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [commentID, resourceType, resourceID])
  }

  /**
   * Renders the comment editor.
   * @returns {ReactNode} Comment editor content.
   */
  render() {
    const {loading, saving, status} = this.s
    const {commentID} = this.tt
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="commentEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="commentEditScreen"
      >
        <View
          testID="commentEditScreen/content"
        >
          <Text
            style={styles.screenTitle ||= {
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 16
            }}
            testID="commentEditScreen/title"
          >
            {commentID ? _("Edit comment") : _("New comment")}
          </Text>
          <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
            <Card
              style={styles.formCard ||= {
                gap: 12,
                maxWidth: 520,
                width: "100%"
              }}
              testID="commentEditScreen/form"
            >
              <FormTextInput
                defaultValue={
                  this.s.comment ? this.s.comment.comment() || "" : this.tt.defaultComment
                }
                label={_("Comment")}
                multiline
                name="comment"
                testID="commentEditScreen/commentInput"
              />
              {this.s.task && !commentID &&
                <FormSelect
                  defaultValue={
                    this.s.taskState || this.s.task.state() || "open"
                  }
                  label={_("State")}
                  name="taskState"
                  options={taskStateOptions()}
                  testID="commentEditScreen/taskStateSelect"
                />
              }
              {status &&
                <Text
                  style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                    color: status.kind === "success" ? this.tt.colors.successText : this.tt.colors.dangerText,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="commentEditScreen/statusMessage"
                >
                  {status.text}
                </Text>
              }
              <Button
                disabled={saving}
                iconName="floppy-disk"
                label={saving ? _("Saving...") : _("Save")}
                onPress={this.tt.onSubmit}
                testID="commentEditScreen/saveButton"
              />
            </Card>
          </Form>
        </View>
      </View>
    )
  }

  /**
   * Saves the comment form.
   * @returns {Promise<void>} Resolves when save handling completes.
   * @rejects {unknown} When saving fails.
   */
  onSubmit = async () => {
    const {commentID, params, router} = this.tt
    const {comment, task, taskState} = this.s
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const values = formObject.asObject()
    this.s.saving = true
    this.s.status = null
    try {
      let commentText = values.comment || ""
      const selectedTaskState = values.taskState || taskState

      // Offline: queue the append-only comment (and, when the form carries a
      // different state, the atomic comment+state-change intent) against the
      // local replica. Editing a synchronized historical comment stays
      // online-only in v1, so only the create flow takes the offline path.
      if (!this.online) {
        if (comment && commentID) throw new Error(_("Comments can only be edited while connected"))
        if (!this.replica || !task) throw new Error(_("Comments can only be queued offline from a synced task"))
        if (!commentText.trim()) throw new Error(_("Comment cannot be blank"))
        if (selectedTaskState && task.state() !== selectedTaskState) {
          await queueOfflineCommentCreateWithStateChange({
            comment: commentText,
            replica: this.replica,
            taskId: String(task.id()),
            taskStateChange: {
              baseVersion: task.updatedAt() ? new Date(task.updatedAt()).toISOString() : null,
              state: selectedTaskState
            }
          })
        } else {
          await queueOfflineCommentCreate({comment: commentText, replica: this.replica, taskId: String(task.id())})
        }
        this.s.status = {kind: "success", text: _("Comment queued for sync")}
        this.s.saving = false
        if (task) {
          const projectID = task.projectId()
          const projectQuery = projectID ? `?projectId=${encodeURIComponent(projectID)}` : ""
          router.push(buildRoutePath(params, `/tasks/${String(task.id())}${projectQuery}`))
        }
        return
      }

      if (task && selectedTaskState && task.state() !== selectedTaskState) {
        const translatedState = translatedTaskState(selectedTaskState)
        commentText = `${commentText}<div style="padding-top: 15px; padding-bottom: 15px;">${_(
          "Changed the task status to %{state}",
          {
            state: translatedState
          }
        )}</div>`
        await task.update({
          state: selectedTaskState
        })
      }
      let finalResourceId
      let finalResourceType
      if (comment && commentID) {
        await comment.update({
          comment: commentText
        })
        finalResourceId = String(comment.resourceId())
        finalResourceType = comment.resourceType()
      } else {
        const createResourceID = requiredRouteParam(params, "resourceId", "Resource ID")
        const createResourceType = requiredRouteParam(params, "resourceType", "Resource type")
        if (!createResourceID.trim()) throw new Error(_("Resource ID cannot be blank"))

        const createResourceId = createResourceID.trim()

        await Comment.create({
          comment: commentText,
          resourceId: createResourceId,
          resourceType: createResourceType
        })
        finalResourceId = String(createResourceId)
        finalResourceType = createResourceType
      }
      if (finalResourceType === "Task") {
        const projectID = task ? task.projectId() : null
        const projectQuery = projectID ? `?projectId=${encodeURIComponent(projectID)}` : ""
        router.push(buildRoutePath(params, `/tasks/${finalResourceId}${projectQuery}`))
      } else {
        router.push(buildRoutePath(params, "/"))
      }
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<CommentsEditScreenProps>} */
const CommentsEditScreenComponent = memo(shapeComponent(CommentsEditScreen))

export default CommentsEditScreenComponent
